import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { MagnifyingGlass, Info, CaretDown } from '@phosphor-icons/react';
import { useSyncedPreference } from '@/hooks/useSyncedPreference';
import CoinIcon from '@/components/ui/coin-icon';
import { fmtFixed, fmtPct, intlLocale, withSign } from '@/lib/format';
import { localiseDecimal, withUsd } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { AssetClassBadge } from '@/components/AssetType';
import { useAssets } from '@/hooks/useAssets';
import { type AssetClass, instrumentOf, shownClass, useInstruments } from '@/lib/instruments';

/****************
 * Live Prices — the user's own watchlist.
 *
 * Guillaume, 2026-09-03: "Total1, Total2 and Total3, Gold, DXY, I told you to
 * add these data to the Live Prices chart, this chart should be customizable,
 * contain more crypto and have the option to decide what is displayed there."
 *
 * So this table holds two kinds of row and does not pretend otherwise:
 *
 *   COINS  price, 24h, market cap, 24h volume — a hundred of them to choose
 *          from rather than the twenty the price socket hardcodes.
 *   INDEX  the market caps (TOTAL, TOTAL2, TOTAL3) and the macro tickers
 *          (dollar index, gold). No market cap of their own, because the
 *          question is meaningless for them — the cell is a dash, not a zero.
 *
 * ── Where each number comes from ────────────────────────────────────────────
 *
 * Prices come from CoinGecko every five minutes. Where the websocket carries a
 * symbol its price wins, because it is live and CoinGecko is minutes old, and
 * those rows are marked so the difference is visible rather than implied. The
 * socket covers about twenty symbols today — widening it is FD-9 — and this
 * table deliberately does not wait for that: a user can watch a coin we do not
 * stream, it just updates every five minutes instead of every second.
 ****************/

const STORAGE_KEY = 'copyfi.livePrices.rows';

/** What a user gets before they choose. Deliberately mixed, so the two row
 *  kinds are discoverable rather than hidden behind the picker. */
const DEFAULT_ROWS = [
  'BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT', 'XRPUSDT', 'DOGEUSDT',
  'TOTAL', 'TOTAL2', 'DXY', 'GOLD', 'SPX', 'WTI',
];

/*
 * The reference rows from Yahoo (market_endpoints.go macroSymbols): their
 * key in a watchlist, their type, and the register symbol of the asset page
 * they open. The S&P 500, Nasdaq 100, WTI and VIX joined the dollar and gold
 * on 2026-10-06 (the multi-asset Market section).
 */
const MACRO_ROWS: Record<string, { key: string; cls: AssetClass; page: string | null; volume: boolean }> = {
  'DX-Y.NYB': { key: 'DXY', cls: 'stock', page: 'DXYUSDT', volume: false }, // the dollar index trades like an ETF
  'GC=F': { key: 'GOLD', cls: 'commodity', page: 'XAUUSDT', volume: true },
  '^GSPC': { key: 'SPX', cls: 'stock', page: 'SPXUSDT', volume: true },
  '^NDX': { key: 'NDX', cls: 'stock', page: 'NAS100USDT', volume: true },
  'CL=F': { key: 'WTI', cls: 'commodity', page: 'CLUSDT', volume: true },
  '^VIX': { key: 'VIX', cls: 'stock', page: 'VIXUSDT', volume: false },
};

/*
 * ONE ROW PER ASSET (Guillaume, 2026-10-06: "We have Gold and XAU as separate
 * assets, it's not necessary and confusing"). A reference row (Yahoo's gold,
 * WTI, S&P...) and the Assets list's row for the same asset were two choices;
 * the reference row now stands for its asset, the asset's own row leaves the
 * picker, and a saved list holding either reads as the one row.
 */
export const ASSET_TO_REFERENCE: Record<string, string> = Object.fromEntries(
  Object.values(MACRO_ROWS).filter((d) => d.page).map((d) => [d.page as string, d.key]),
);

/** A saved watchlist with one key per row, in its order: an asset's own key
 *  becomes the reference row standing for it, and a repeat is dropped. */
export function oneKeyPerRow(keys: string[]): string[] {
  return [...new Set(keys.map((k) => ASSET_TO_REFERENCE[k] ?? k))];
}

interface CoinRow {
  symbol: string;
  name: string;
  image?: string;
  price: number;
  change_24h_pct: number;
  market_cap: number;
  volume_24h: number;
  rank: number;
}

interface MacroQuote {
  symbol: string;
  name: string;
  price: number;
  change_24h_pct: number;
  volume?: number;
  day_low?: number;
  day_high?: number;
  prev_close?: number;
  ok: boolean;
}

interface Caps {
  ok: boolean;
  total: number;
  total2: number;
  total3: number;
  change_24h_pct: number;
  volume_24h: number;
  volume2?: number;
  volume3?: number;
}

interface Row {
  key: string;
  label: string;
  /** What it is, shown small beside the ticker: "XAU (Gold)" (2026-10-06). */
  name?: string;
  kind: 'coin' | 'index';
  price: number;
  change: number | null;
  /* Two stats per row, each with its OWN label.
   *
   * Guillaume: "Gold and Total and DXY still lack Cap and/or Vol." Some of
   * those quantities do not exist — gold has no market cap and the dollar index
   * has neither — so the honest fix is not to invent them but to ask each row
   * the question it CAN answer. A coin reports cap and volume; an index reports
   * its day range and yesterday's close. Both cells carry something true. */
  /* `label` is a translation key (dashboard namespace). */
  statA: { label: string; value: string } | null;
  statB: { label: string; value: string } | null;
  live: boolean;
  /** Market-cap rank, for the picker. Null for an index, which has none. */
  rank: number | null;
  /** The row's own logo. Coins get CoinGecko's; index rows get a drawn mark. */
  image?: string;
  /** Asset type and asset page (Assets section, 2026-10-04). */
  cls: AssetClass;
  slug?: string | null;
}

/*
 * Marks for the index rows.
 *
 * Guillaume asked for images, and got emoji in the first pass — which filled
 * the blank without being what he asked for. These are drawn: a globe for the
 * whole market, coin stacks for the two alt caps, a dollar for the index, a bar
 * for gold. Inline SVG rather than files, so they inherit the row's size and
 * cannot 404 the way a remote logo can.
 */
function IndexMark({ kind, size = 16 }: { kind: string; size?: number }) {
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none' as const };
  if (kind === 'DXY') {
    return (
      <svg {...common} aria-hidden>
        <circle cx="12" cy="12" r="10" fill="hsl(152, 45%, 30%)" />
        <path d="M12 6v12M9.5 9a2.5 2.5 0 0 1 5 0c0 3-5 1.5-5 4.5a2.5 2.5 0 0 0 5 0"
          stroke="hsl(152, 70%, 80%)" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === 'GOLD') {
    return (
      <svg {...common} aria-hidden>
        <rect x="2" y="13" width="9" height="6" rx="1.2" fill="hsl(43, 88%, 52%)" />
        <rect x="13" y="13" width="9" height="6" rx="1.2" fill="hsl(43, 78%, 45%)" />
        <rect x="7.5" y="6" width="9" height="6" rx="1.2" fill="hsl(43, 95%, 62%)" />
      </svg>
    );
  }
  if (kind === 'SPX' || kind === 'NDX') {
    // An index: a rising line.
    return (
      <svg {...common} aria-hidden>
        <circle cx="12" cy="12" r="10" fill={kind === 'SPX' ? 'hsl(217, 60%, 32%)' : 'hsl(200, 60%, 30%)'} />
        <path d="M6 15.5l3.5-3.5 3 2.5L18 8.5" stroke="hsl(200, 90%, 82%)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }
  if (kind === 'WTI') {
    // Oil: a drop.
    return (
      <svg {...common} aria-hidden>
        <circle cx="12" cy="12" r="10" fill="hsl(142, 35%, 24%)" />
        <path d="M12 6c2.2 3 3.6 5 3.6 7a3.6 3.6 0 0 1-7.2 0c0-2 1.4-4 3.6-7z" fill="hsl(0, 0%, 8%)" stroke="hsl(142, 60%, 70%)" strokeWidth="1" />
      </svg>
    );
  }
  if (kind === 'VIX') {
    // Volatility: a jagged line.
    return (
      <svg {...common} aria-hidden>
        <circle cx="12" cy="12" r="10" fill="hsl(270, 40%, 30%)" />
        <path d="M5.5 13l2.5-4 2.5 6 2.5-7 2.5 6 2.5-3" stroke="hsl(270, 85%, 82%)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
    );
  }
  if (kind === 'TOTAL') {
    return (
      <svg {...common} aria-hidden>
        <circle cx="12" cy="12" r="9.5" fill="hsl(217, 80%, 45%)" />
        <ellipse cx="12" cy="12" rx="4.2" ry="9.5" stroke="hsl(217, 90%, 85%)" strokeWidth="1.2" />
        <path d="M2.8 9h18.4M2.8 15h18.4" stroke="hsl(217, 90%, 85%)" strokeWidth="1.2" />
      </svg>
    );
  }
  // TOTAL2 / TOTAL3 — the market minus its biggest pieces, drawn as a ring with
  // a bite taken out of it.
  const dark = kind === 'TOTAL3';
  return (
    <svg {...common} aria-hidden>
      <circle cx="12" cy="12" r="9.5" fill={dark ? 'hsl(280, 55%, 42%)' : 'hsl(262, 70%, 52%)'} />
      <path d="M12 2.5A9.5 9.5 0 0 1 21.5 12H12z" fill="hsl(220, 15%, 16%)" />
      {dark && <path d="M12 12h9.5A9.5 9.5 0 0 1 12 21.5z" fill="hsl(220, 15%, 22%)" />}
    </svg>
  );
}

const num = (v: number) =>
  v >= 1000 ? v.toLocaleString(intlLocale(), { maximumFractionDigits: 0 }) : fmtFixed(v, 2);

const compact = (v: number) => {
  const a = Math.abs(v);
  if (a >= 1e12) return withUsd(`${fmtFixed(v / 1e12, 2)}T`);
  if (a >= 1e9) return withUsd(`${fmtFixed(v / 1e9, 1)}B`);
  if (a >= 1e6) return withUsd(`${fmtFixed(v / 1e6, 1)}M`);
  if (a >= 1e3) return withUsd(`${fmtFixed(v / 1e3, 1)}k`);
  return withUsd(fmtFixed(v, 0));
};

function price(v: number): string {
  if (v >= 1000) return v.toLocaleString(intlLocale(), { maximumFractionDigits: 2 });
  if (v >= 1) return fmtFixed(v, 2);
  if (v >= 0.01) return fmtFixed(v, 4);
  return localiseDecimal(v.toPrecision(4));
}

interface LivePricesTableProps {
  coins: CoinRow[];
  caps?: Caps;
  macro: MacroQuote[];
  /** Symbol -> price from the websocket, where it carries one. */
  livePrices: Record<string, number>;
  loading: boolean;
}

export function LivePricesTable({ coins, caps, macro, livePrices, loading }: LivePricesTableProps) {
  /*
   * Stored against the USER, not the browser, so the arrangement made here is
   * the arrangement on the phone. The local copy is still written underneath
   * and is what renders before the server answers -- see useSyncedPreference.
   */
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  /*
   * Every asset CopyFi recorded can be a row (Guillaume, 2026-10-04: "every
   * asset ever recorded ... with their current market price, 24h change,
   * volume, market cap, LOGO"), filtered by type, and a row opens its asset
   * page the way a trader row opens the trader.
   */
  const { data: assetData } = useAssets();
  const assets = assetData?.assets;
  // The name adjuster: every row's name and type come from the one register.
  const { data: instrumentReg } = useInstruments();
  /** A register class as a Live Prices type: indexes sit with stocks (2026-10-05). */
  const priceClass = (symbol: string): AssetClass => {
    const c = instrumentOf(symbol, instrumentReg).class;
    return c === 'indicator' ? 'stock' : c;
  };
  const [savedCls, setCls] = useSyncedPreference<AssetClass | 'all'>('copyfi.livePrices.class', 'all');
  const cls = shownClass(savedCls);
  /*
   * Built once per asset list, NOT per price tick (2026-10-04, Guillaume:
   * "Rendering is struggling A LOT"). The rows below are rebuilt on every tick
   * of the price socket; looking each coin up by scanning ~950 assets, and
   * rebuilding ~950 asset rows, on every tick is what made the page crawl.
   */
  const slugBySymbol = useMemo(() => {
    // Empty until the list arrives: a row without a page is simply not clickable.
    const m = new Map<string, string>();
    if (assets) for (const a of assets) {
      m.set(a.canonical, a.slug);
      for (const s of a.symbols) m.set(s, a.slug);
    }
    return m;
  }, [assets]);
  const assetRows = useMemo<Row[]>(() => {
    if (!assets) return [];
    const out: Row[] = [];
    for (const a of assets) {
      if (a.price == null) continue;
      out.push({
        key: a.canonical,
        label: a.display,
        name: a.name !== a.display ? a.name : undefined,
        kind: 'coin',
        cls: a.class,
        slug: a.slug,
        price: a.price,
        change: a.change_24h_pct,
        statA: a.market_cap ? { label: 'prices.stat.cap', value: compact(a.market_cap) } : null,
        statB: a.volume_24h ? { label: 'prices.stat.vol', value: compact(a.volume_24h) } : null,
        live: false,
        rank: null,
        image: a.logo || undefined,
      });
    }
    return out;
  }, [assets]);
  const [selected, setSelected] = useSyncedPreference<string[]>(STORAGE_KEY, DEFAULT_ROWS);
  const [search, setSearch] = useState('');

  // Kept so the call sites below read unchanged; the hook does the persisting.
  const save = (next: string[]) => next;
  // A saved list holding an asset's own key (XAUUSDT) is rewritten once to
  // the reference row that stands for it (GOLD), duplicates dropped, so the
  // picker, the order and the drag all read one key per row.
  useEffect(() => {
    const normal = oneKeyPerRow(selected);
    if (normal.length !== selected.length || normal.some((k, i) => k !== selected[i])) setSelected(normal);
  }, [selected, setSelected]);
  // A saved key and the reference row standing for it are one choice.
  const sameRow = (k: string, key: string) => k === key || ASSET_TO_REFERENCE[k] === key;
  const isSelected = (key: string) => selected.some((k) => sameRow(k, key));
  const toggle = (key: string) =>
    setSelected((prev) => save(prev.some((k) => sameRow(k, key)) ? prev.filter((k) => !sameRow(k, key)) : [...prev, key]));

  /*
   * Drag a row to reorder the watchlist.
   *
   * Guillaume: "Make their table dragable so the user can swap the order of the
   * assets." The rows reorder live as the pointer crosses one, so the list under
   * the cursor IS the result rather than a preview of it, and the order is
   * written to storage ONCE on drop — an interrupted drag saves nothing
   * half-finished. Same shape as the leaderboard's column drag, deliberately:
   * two different reordering gestures in one product is one too many.
   */
  const [dragKey, setDragKey] = useState<string | null>(null);
  const moveTo = (key: string, to: number) => {
    setSelected((prev) => {
      const from = prev.indexOf(key);
      if (from < 0 || to < 0 || to >= prev.length || from === to) return prev;
      const next = [...prev];
      next.splice(to, 0, next.splice(from, 1)[0]);
      return next;
    });
  };
  const commitOrder = () => {
    setDragKey(null);
    setSelected((prev) => save(prev));
  };

  // Every row a user could pick, in one list, so the picker does not have to
  // know which kind is which.
  const available = useMemo<Row[]>(() => {
    const out: Row[] = [];

    if (caps?.ok) {
      out.push(
        /* A TOTAL row's value IS a market cap, so the cap cell repeats it
           rather than sitting empty — the number exists, it was just not being
           put in the column that asks for it. Only TOTAL has a volume:
           CoinGecko publishes the whole market's, not the ex-BTC slice's, and
           splitting it by dominance would be a guess wearing a figure's
           clothes. */
        { key: 'TOTAL', label: 'TOTAL', kind: 'index', cls: 'crypto', price: caps.total, change: caps.change_24h_pct, live: false, rank: null,
          statA: { label: 'prices.stat.cap', value: compact(caps.total) },
          statB: caps.volume_24h ? { label: 'prices.stat.vol', value: compact(caps.volume_24h) } : null },
        { key: 'TOTAL2', label: 'TOTAL2', kind: 'index', cls: 'crypto', price: caps.total2, change: caps.change_24h_pct, live: false, rank: null,
          statA: { label: 'prices.stat.cap', value: compact(caps.total2) },
          statB: caps.volume2 ? { label: 'prices.stat.vol', value: compact(caps.volume2) } : null },
        { key: 'TOTAL3', label: 'TOTAL3', kind: 'index', cls: 'crypto', price: caps.total3, change: caps.change_24h_pct, live: false, rank: null,
          statA: { label: 'prices.stat.cap', value: compact(caps.total3) },
          statB: caps.volume3 ? { label: 'prices.stat.vol', value: compact(caps.volume3) } : null },
      );
    }
    const displayOf = new Map<string, string>();
    if (assets) for (const a of assets) displayOf.set(a.canonical, a.display);
    for (const m of macro) {
      const def = MACRO_ROWS[m.symbol];
      if (!m.ok || !def) continue;
      const ticker = def.page ? displayOf.get(def.page) : undefined;
      out.push({
        key: def.key,
        label: ticker ?? m.name,
        name: ticker && ticker !== m.name ? m.name : undefined,
        kind: 'index',
        cls: def.page ? priceClass(def.page) : def.cls,
        slug: def.page ? slugBySymbol.get(def.page) ?? null : null,
        price: m.price,
        change: m.change_24h_pct,
        // Neither has a market cap, and only gold has a volume. What both DO
        // have is a day's range and a previous close, so those are what these
        // rows report.
        statA: m.day_low && m.day_high
          ? { label: 'prices.stat.range', value: `${num(m.day_low)}–${num(m.day_high)}` }
          : null,
        statB: !def.volume || !m.volume
          ? (m.prev_close ? { label: 'prices.stat.prev', value: num(m.prev_close) } : null)
          : { label: 'prices.stat.vol', value: m.volume.toLocaleString(intlLocale()) },
        live: false,
        rank: null,
      });
    }
    for (const c of coins) {
      const wsPrice = livePrices[c.symbol];
      out.push({
        key: c.symbol,
        label: instrumentOf(c.symbol, instrumentReg).display,
        name: c.name && c.name.toUpperCase() !== instrumentOf(c.symbol, instrumentReg).display.toUpperCase() ? c.name : undefined,
        kind: 'coin',
        // The socket wins where it has an opinion: it is live, CoinGecko is
        // five minutes old.
        price: wsPrice && wsPrice > 0 ? wsPrice : c.price,
        change: c.change_24h_pct,
        statA: c.market_cap ? { label: 'prices.stat.cap', value: compact(c.market_cap) } : null,
        statB: c.volume_24h ? { label: 'prices.stat.vol', value: compact(c.volume_24h) } : null,
        live: !!(wsPrice && wsPrice > 0),
        rank: c.rank,
        image: c.image,
        cls: priceClass(c.symbol),
        slug: slugBySymbol.get(c.symbol) ?? null,
      });
    }
    // Every other recorded asset, with the market the Assets section holds --
    // added once the list has arrived (assetRows is empty until then); the
    // rows above stand alone meanwhile, and nothing here claims there are no
    // other assets.
    const held = new Set(out.map((r) => r.key));
    for (const r of assetRows) {
      if (!held.has(r.key) && !ASSET_TO_REFERENCE[r.key]) out.push(r);
    }
    return out;
  }, [coins, caps, macro, livePrices, slugBySymbol, assetRows, assets, instrumentReg]);

  const byKey = useMemo(() => new Map(available.map((r) => [r.key, r])), [available]);
  // The user's ORDER, not the source's — the picker appends, so the list reads
  // in the order things were added.
  const rows = useMemo(() => {
    const keys = oneKeyPerRow(selected);
    return (keys.map((k) => byKey.get(k)).filter(Boolean) as Row[])
      .filter((r) => cls === 'all' || r.cls === cls);
  }, [selected, byKey, cls]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const ofType = cls === 'all' ? available : available.filter((r) => r.cls === cls);
    if (!q) return ofType;
    return ofType.filter((r) => r.key.toLowerCase().includes(q) || r.label.toLowerCase().includes(q)
      || (r.name ?? '').toLowerCase().includes(q));
  }, [available, search, cls]);

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center flex-wrap justify-between mb-3 gap-2">
        <div className="flex items-center gap-1.5 shrink-0">
          <h2 className="text-base font-semibold text-foreground whitespace-nowrap">{t('prices.title')}</h2>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-muted-foreground cursor-help"><Info size={12} /></span>
            </TooltipTrigger>
            <TooltipContent className="max-w-[280px] text-xs">
              {t('prices.info')}
            </TooltipContent>
          </Tooltip>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
        {/* One "Select Asset" button holding All / Crypto / Stocks-ETFs / Commodities, the
            rows straight under the title (Guillaume's /preview review #23, 10-24). */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5 shrink-0">
              {cls === 'all' ? t('prices.selectAsset') : t(`common:asset.split.${cls}`)} <CaretDown size={11} />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-44 p-1">
            {(['all', 'crypto', 'stock', 'commodity'] as const).map((o) => (
              <button
                key={o}
                type="button"
                onClick={() => setCls(o)}
                className={`w-full text-start text-xs px-2 py-1.5 rounded ${cls === o ? 'bg-white/10 text-foreground' : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'}`}
              >
                {t(`common:asset.split.${o}`)}
              </button>
            ))}
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 text-xs gap-1.5 shrink-0">
              {t('prices.rows')} <Badge variant="secondary" className="h-4 px-1 text-[10px]">{rows.length}</Badge>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 p-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold">{t('prices.showRows')}</p>
              <Button
                variant="ghost" size="sm" className="h-6 px-2 text-[10px]"
                onClick={() => setSelected(save(DEFAULT_ROWS))}
              >
                {t('prices.reset')}
              </Button>
            </div>

            <div className="relative mb-2">
              <MagnifyingGlass size={13} className="absolute start-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('prices.search')}
                className="h-7 ps-7 text-xs"
              />
            </div>

            <div className="max-h-[300px] overflow-y-auto space-y-0.5">
              {filtered.map((r) => (
                <label key={r.key} className="flex items-center gap-2 text-xs cursor-pointer py-1 px-1 rounded hover:bg-muted/50">
                  <Checkbox
                    checked={isSelected(r.key)}
                    onCheckedChange={() => toggle(r.key)}
                    aria-label={isSelected(r.key) ? t('mobile:leaderboard.hideColumn', { label: r.label }) : undefined}
                  />
                  <span className="flex-1 truncate">
                    {r.label}{r.name && <span className="text-muted-foreground"> ({r.name})</span>}
                  </span>
                  {r.kind === 'index' ? (
                    <span className="text-[9px] text-muted-foreground uppercase">{t('prices.index')}</span>
                  ) : r.rank ? (
                    <span className="text-[9px] text-muted-foreground">#{r.rank}</span>
                  ) : (
                    <AssetClassBadge cls={r.cls} />
                  )}
                </label>
              ))}
              {filtered.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">{t('prices.noMatch', { search })}</p>
              )}
            </div>
          </PopoverContent>
        </Popover>
        </div>
      </div>

      {/* As tall as the Market section beside it (Guillaume, 2026-10-06); more
          rows scroll inside, under a header that stays. */}
      <div className="glass-card rounded-xl overflow-y-auto flex-1 min-h-0">
        <div className="sticky top-0 z-10 bg-card grid grid-cols-[1fr_auto_auto] gap-x-3 px-3 py-2 border-b border-border/40 text-[10px] uppercase tracking-wide text-muted-foreground">
          <span>{t('prices.asset')} <span className="normal-case tracking-normal opacity-60">{t('prices.dragToReorder')}</span></span>
          <span className="text-end">{t('prices.price')}</span>
          <span className="text-end w-14">{t('prices.24h')}</span>
        </div>

        <div className="divide-y divide-border/30">
          {loading && rows.length === 0 &&
            Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="px-3 py-2"><Skeleton className="h-4 w-full" /></div>
            ))}

          {rows.map((r, idx) => (
            <div
              key={r.key}
              draggable
              onDragStart={(e) => {
                setDragKey(r.key);
                e.dataTransfer.effectAllowed = 'move';
                // Firefox starts no drag at all without a payload.
                e.dataTransfer.setData('text/plain', r.key);
              }}
              onDragEnter={() => { if (dragKey && dragKey !== r.key) moveTo(dragKey, idx); }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); commitOrder(); }}
              onDragEnd={commitOrder}
              onClick={() => { if (r.slug) navigate(`/assets/${r.slug}`); }}
              className={`px-3 py-2 cursor-grab active:cursor-grabbing select-none ${
                dragKey === r.key ? 'opacity-40 bg-muted' : 'hover:bg-white/[0.02]'
              }`}
            >
              <div className="grid grid-cols-[1fr_auto_auto] gap-x-3 items-center">
                <div className="flex items-center gap-1.5 min-w-0">
                  {r.kind === 'index' ? (
                    <span className="shrink-0 leading-none"><IndexMark kind={r.key} /></span>
                  ) : r.image ? (
                    /* The source's own logo. CoinIcon stays as the fallback for
                       anything the coin list does not carry. */
                    <img
                      src={r.image}
                      alt=""
                      width={16}
                      height={16}
                      loading="lazy"
                      /* An image is draggable BY DEFAULT, and grabbing a row by
                         its logo therefore started dragging the picture instead
                         of the row -- the row's own dragstart never fired, so
                         nothing reordered. This is why the drag "did not work"
                         for exactly the rows that had a logo. */
                      draggable={false}
                      className="rounded-full shrink-0 pointer-events-none"
                      style={{ width: 16, height: 16 }}
                    />
                  ) : (
                    /* Full symbol, not a pre-stripped one — stripping twice is
                       what erased USDC's icon. */
                    <span className="pointer-events-none"><CoinIcon symbol={r.key} size={16} /></span>
                  )}
                  <span className="text-xs font-medium truncate" title={r.name ? `${r.label} (${r.name})` : r.label}>
                    {r.label}{r.name && <span className="text-[10px] font-normal text-muted-foreground"> ({r.name})</span>}
                  </span>
                  {r.live && (
                    <span className="text-[8px] text-success/80 uppercase tracking-wide shrink-0">{t('prices.live')}</span>
                  )}
                </div>
                <span className="text-xs font-mono text-end">
                  {r.kind === 'index' && r.key.startsWith('TOTAL') ? compact(r.price) : withUsd(price(r.price))}
                </span>
                <span className={`text-xs font-mono text-end w-14 ${
                  r.change === null ? 'text-muted-foreground' : r.change >= 0 ? 'text-success' : 'text-danger'
                }`}>
                  {r.change === null ? '—' : `${withSign(r.change >= 0, fmtPct(r.change === 0 ? 0 : r.change, 2))}`}
                </span>
              </div>

              {/* Cap and volume on a second line rather than two more columns:
                  the table sits in a narrow left rail and four numeric columns
                  would truncate the one people came for. A dash where the
                  question does not apply — TOTAL has no market cap of its own. */}
              <div className="flex gap-3 text-[10px] text-muted-foreground mt-0.5 ps-[22px]">
                {r.statA && <span>{t(r.statA.label)} {r.statA.value}</span>}
                {r.statB && <span>{t(r.statB.label)} {r.statB.value}</span>}
              </div>
            </div>
          ))}

          {!loading && rows.length === 0 && (
            <div className="px-3 py-8 text-center">
              <p className="text-xs text-muted-foreground">{t('prices.noRows')}</p>
              <p className="text-[10px] text-muted-foreground/70 mt-1">{t('prices.useRows')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
